import React, { useState, useRef, useEffect } from "react";
import { FaCircle, FaStopCircle } from "react-icons/fa";
import html2canvas from "html2canvas";

const RecordMeeting = ({ roomId, remoteStreams = [] }) => {
    const [isRecording, setIsRecording] = useState(false);
    const [mediaRecorder, setMediaRecorder] = useState(null);
    const [chunks, setChunks] = useState([]);
    const canvasRef = useRef(null);
    const audioContextRef = useRef(null);

    const updateCanvas = async () => {
        try {
            const appBody = document.body; // Target your app's body or specific element
            const snapshot = await html2canvas(appBody);
            const ctx = canvasRef.current.getContext("2d");
            ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
            ctx.drawImage(snapshot, 0, 0, canvasRef.current.width, canvasRef.current.height);
        } catch (error) {
            console.error("Error capturing canvas:", error);
        }
    };

    const handleRecordingToggle = () => {
        if (isRecording) {
            stopRecording();
        } else {
            startRecording();
        }
    };

    const startRecording = async () => {
        try {
            const canvas = canvasRef.current;
            const videoStream = canvas.captureStream(30); // 30 FPS
            const localAudioStream = await navigator.mediaDevices.getUserMedia({ audio: true });

            audioContextRef.current = new AudioContext();
            const audioContext = audioContextRef.current;
            const destination = audioContext.createMediaStreamDestination();

            // Connect local audio
            const localSource = audioContext.createMediaStreamSource(localAudioStream);
            localSource.connect(destination);

            // Connect remote audio
            remoteStreams.forEach((remoteStream) => {
                if (remoteStream instanceof MediaStream) {
                    const remoteSource = audioContext.createMediaStreamSource(remoteStream);
                    remoteSource.connect(destination);
                }
            });

            // Combine video and audio streams
            const combinedStream = new MediaStream([
                ...videoStream.getVideoTracks(),
                ...destination.stream.getAudioTracks(),
            ]);

            const recorder = new MediaRecorder(combinedStream, { mimeType: "video/webm; codecs=vp8,opus" });

            const recordedChunks = [];
            recorder.ondataavailable = (event) => {
                if (event.data.size > 0) {
                    recordedChunks.push(event.data);
                }
            };

            recorder.onstop = () => {
                setChunks(recordedChunks);
                downloadRecording(recordedChunks);
                cleanup();
            };

            recorder.start();
            setMediaRecorder(recorder);
            setIsRecording(true);

            // Start canvas update only during recording
            const intervalId = setInterval(updateCanvas, 500);
            canvasRef.current._intervalId = intervalId;

        } catch (error) {
            console.error("Error starting recording:", error);
        }
    };

    const stopRecording = () => {
        if (mediaRecorder) {
            mediaRecorder.stop();
        }
        setIsRecording(false);

        // Stop canvas updates
        clearInterval(canvasRef.current._intervalId);
    };

    const downloadRecording = (chunks) => {
        const blob = new Blob(chunks, { type: "video/webm" });
        const url = URL.createObjectURL(blob);

        const a = document.createElement("a");
        a.href = url;
        a.download = `recording-${Date.now()}.webm`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);

        URL.revokeObjectURL(url);
        // alert("Recording downloaded successfully!");
    };

    const cleanup = () => {
        if (audioContextRef.current) {
            audioContextRef.current.close();
        }
        setChunks([]);
        setMediaRecorder(null);
    };

    useEffect(() => {
        return () => {
            // Cleanup on unmount
            if (audioContextRef.current) {
                audioContextRef.current.close();
            }
            clearInterval(canvasRef.current?._intervalId);
        };
    }, []);

    return (
        <div>
            {/* Hidden Canvas for Capturing Content */}
            <canvas
                ref={canvasRef}
                width={1920}
                height={1080}
                style={{ display: "none" }}
            ></canvas>

            {/* Record Button */}
            <div
                className="video_button_container"
                onClick={handleRecordingToggle}
                style={{ cursor: "pointer" }}
            >
                {isRecording ? (
                    <FaStopCircle
                        className="video_button_icon stop"
                        title="Stop Recording"
                        size={40}
                        color="red"
                    />
                ) : (
                    <FaCircle
                        className="video_button_icon start"
                        title="Start Recording"
                        size={40}
                        color="green"
                    />
                )}
            </div>
        </div>
    );
};

export default RecordMeeting;
